/**
 * Kênh nạp tiền (docs/design/98-nap-tien.md): cấu hình, đơn nạp, giao dịch ngân hàng báo về và lệnh test.
 * Lệnh test đi qua đúng code xử lý webhook SePay nên thư Pet Coin được gửi thật; chỉ hiện khi server.json bật
 * paymentTestMode và tài khoản có quyền payment.test.
 */

import { useState } from "react";
import PropTypes from "prop-types";
import { Link, useSearchParams } from "react-router-dom";
import Card from "@mui/material/Card";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Alert from "@mui/material/Alert";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import PageGuide from "components/admin/PageGuide";
import PageCard from "components/admin/PageCard";
import AdminTable from "components/admin/AdminTable";
import ActionDialog from "components/admin/ActionDialog";
import SelectField from "components/admin/SelectField";
import { useFetch, useRunner } from "components/admin/hooks";
import { useConfirm } from "components/admin/Feedback";
import api from "api/client";
import { formatNumber, formatDate } from "utils/format";

const ORDER_STATUS = {
  waiting: { label: "Chờ chuyển khoản", color: "warning" },
  paid: { label: "Thành công", color: "success" },
  cancelled: { label: "Đã hủy", color: "default" },
};

const NOTIFY_COLOR = { 0: "error", 1: "success", 2: "warning", 3: "default" };

const RESULT_TEXT = {
  Credited: "Đã ghi nhận, chờ game server gửi thư Pet Coin",
  Duplicate: "Trùng mã giao dịch: bỏ qua, không cộng lần hai",
  Unmatched: "Không khớp đơn: chờ admin xử lý tay",
  BelowMin: "Dưới mức tối thiểu: không gửi Pet Coin",
  Ignored: "Bỏ qua (tiền ra, số tiền sai hoặc sai tài khoản nhận)",
};

function Stat({ label, value, hint }) {
  return (
    <MDBox>
      <MDTypography variant="caption" color="text" fontWeight="medium">
        {label}
      </MDTypography>
      <MDTypography variant="h5">{value}</MDTypography>
      {hint && (
        <MDTypography variant="caption" color="text">
          {hint}
        </MDTypography>
      )}
    </MDBox>
  );
}

Stat.defaultProps = { hint: null };

Stat.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
  hint: PropTypes.node,
};

function configForm(config) {
  return {
    enabled: Boolean(config.enabled),
    bankCode: config.bankCode || "",
    bankName: config.bankName || "",
    accountNo: config.accountNo || "",
    accountName: config.accountName || "",
    contentPrefix: config.contentPrefix || "",
    minAmount: config.minAmount || 10000,
    orderMinutes: config.orderMinutes || 10,
  };
}

function ConfigCard({ data, onChanged }) {
  const { config, testMode, canEdit } = data;
  const [run, busy] = useRunner();
  const confirm = useConfirm();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(() => configForm(config));
  const set = (patch) => setForm({ ...form, ...patch });
  const hasBank = Boolean(config.bankCode && config.accountNo && config.accountName);
  const bank = config.accountNo
    ? `${config.bankName || config.bankCode} · ${config.accountNo} · ${config.accountName}`
    : "Chưa cấu hình tài khoản ngân hàng";
  const sample = `${config.contentPrefix ? `${config.contentPrefix} ` : ""}${
    config.codePrefix
  }XXXXXX`;
  const formPrefix = form.contentPrefix.trim().toUpperCase();

  const save = async (body, message) => {
    const { ok } = await run(
      () =>
        api.post("/api/admin/payment/config", {
          ...body,
          minAmount: Number(body.minAmount),
          orderMinutes: Number(body.orderMinutes),
        }),
      () => message
    );
    if (ok) {
      setEditing(false);
      onChanged();
    }
  };

  const toggle = async () => {
    const next = !config.enabled;
    const ok = await confirm({
      title: next ? "Bật kênh nạp" : "Tắt kênh nạp",
      message: next
        ? `Người chơi sẽ thấy trang Nạp tiền và mã QR chuyển vào:\n${bank}\n\nKiểm tra đúng số tài khoản trước khi bật.`
        : "Trang Nạp tiền sẽ báo tạm dừng, người chơi không tạo đơn mới được. Tiền đúng mã chuyển tới sau đó vẫn được cộng.",
      color: next ? "success" : "warning",
      confirmText: next ? "Bật kênh nạp" : "Tắt kênh nạp",
    });
    if (!ok) return;
    save({ ...configForm(config), enabled: next }, next ? "Đã bật kênh nạp" : "Đã tắt kênh nạp");
  };

  return (
    <PageCard
      title="Cấu hình kênh nạp"
      subtitle="Có hiệu lực ngay trên website. Khóa SePay nằm trong appsettings của website"
    >
      <MDBox display="flex" gap={1} flexWrap="wrap" alignItems="center" mb={1.5}>
        {canEdit ? (
          <FormControlLabel
            control={
              <Switch
                checked={Boolean(config.enabled)}
                onChange={toggle}
                disabled={busy || (!config.enabled && !hasBank)}
              />
            }
            label={config.enabled ? "Kênh nạp đang bật" : "Kênh nạp đang tắt"}
          />
        ) : (
          <Chip
            size="small"
            color={config.enabled ? "success" : "default"}
            label={config.enabled ? "Kênh nạp đang bật" : "Kênh nạp đang tắt"}
          />
        )}
        <Chip
          size="small"
          color={testMode ? "warning" : "default"}
          label={testMode ? "Chế độ test: bật" : "Chế độ test: tắt"}
        />
      </MDBox>
      {!editing && (
        <>
          <MDTypography variant="button" display="block">
            Ngân hàng: {bank}
          </MDTypography>
          <MDTypography variant="button" display="block">
            Nội dung chuyển khoản: {sample}
          </MDTypography>
          <MDTypography variant="button" display="block">
            Tỉ giá: {formatNumber(config.vndPerCoin)}đ = 1 Pet Coin · Tối thiểu{" "}
            {formatNumber(config.minAmount)}đ
          </MDTypography>
          <MDTypography variant="button" display="block">
            Đơn tự hủy sau {config.orderMinutes} phút
          </MDTypography>
          {canEdit && (
            <MDBox mt={1.5}>
              <MDButton
                size="small"
                variant="outlined"
                color="info"
                onClick={() => {
                  setForm(configForm(config));
                  setEditing(true);
                }}
              >
                Sửa thông tin
              </MDButton>
            </MDBox>
          )}
        </>
      )}
      {editing && (
        <Grid container spacing={1.5}>
          <Grid item xs={6}>
            <MDInput
              fullWidth
              label="Mã ngân hàng (VietQR)"
              value={form.bankCode}
              onChange={(e) => set({ bankCode: e.target.value })}
              helperText="Mã BIN, vd 970415 (VietinBank)"
            />
          </Grid>
          <Grid item xs={6}>
            <MDInput
              fullWidth
              label="Tên ngân hàng hiện cho người chơi"
              value={form.bankName}
              onChange={(e) => set({ bankName: e.target.value })}
            />
          </Grid>
          <Grid item xs={6}>
            <MDInput
              fullWidth
              label="Số tài khoản"
              value={form.accountNo}
              onChange={(e) => set({ accountNo: e.target.value })}
            />
          </Grid>
          <Grid item xs={6}>
            <MDInput
              fullWidth
              label="Chủ tài khoản (IN HOA không dấu)"
              value={form.accountName}
              onChange={(e) => set({ accountName: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              fullWidth
              label="Chữ đứng trước mã trong nội dung"
              value={form.contentPrefix}
              onChange={(e) => set({ contentPrefix: e.target.value })}
              helperText={`VietinBank qua SePay cần SEVQR. Nội dung: ${
                formPrefix ? `${formPrefix} ` : ""
              }${config.codePrefix}XXXXXX`}
            />
          </Grid>
          <Grid item xs={6}>
            <MDInput
              fullWidth
              type="number"
              label="Tối thiểu (đ)"
              value={form.minAmount}
              onChange={(e) => set({ minAmount: e.target.value })}
              inputProps={{ min: 1000, step: 1000 }}
            />
          </Grid>
          <Grid item xs={6}>
            <MDInput
              fullWidth
              type="number"
              label="Giữ mã QR (phút)"
              value={form.orderMinutes}
              onChange={(e) => set({ orderMinutes: e.target.value })}
              inputProps={{ min: 1, max: 1440 }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDBox display="flex" gap={1}>
              <MDButton
                size="small"
                color="info"
                disabled={busy}
                onClick={() => save({ ...form, enabled: config.enabled }, "Đã lưu cấu hình")}
              >
                Lưu
              </MDButton>
              <MDButton size="small" variant="text" color="dark" onClick={() => setEditing(false)}>
                Hủy
              </MDButton>
            </MDBox>
          </Grid>
        </Grid>
      )}
    </PageCard>
  );
}

ConfigCard.propTypes = {
  data: PropTypes.shape({
    config: PropTypes.object,
    testMode: PropTypes.bool,
    canEdit: PropTypes.bool,
  }).isRequired,
  onChanged: PropTypes.func.isRequired,
};

function Overview({ data, onChanged }) {
  const { stats } = data;
  return (
    <Grid container spacing={3} mb={3}>
      <Grid item xs={12} lg={5}>
        <ConfigCard data={data} onChanged={onChanged} />
      </Grid>
      <Grid item xs={12} lg={7}>
        <PageCard title="Hôm nay" subtitle="Chỉ tính tiền thật, không tính giao dịch thử">
          <Grid container spacing={2}>
            <Grid item xs={6} md={4}>
              <Stat label="Đơn người chơi tạo" value={formatNumber(stats.ordersToday)} />
            </Grid>
            <Grid item xs={6} md={4}>
              <Stat label="Lần nạp thành công" value={formatNumber(stats.paidToday)} />
            </Grid>
            <Grid item xs={6} md={4}>
              <Stat
                label="Tiền về"
                value={`${formatNumber(stats.vndToday)}đ`}
                hint={`${formatNumber(stats.coinToday)} Pet Coin`}
              />
            </Grid>
            <Grid item xs={6} md={4}>
              <Stat
                label="Tiền chưa khớp đơn (cần xử lý)"
                value={formatNumber(stats.unmatched)}
                hint="Tất cả các ngày, xử lý ở tab Giao dịch ngân hàng"
              />
            </Grid>
            <Grid item xs={6} md={4}>
              <Stat
                label="Thư Pet Coin chờ gửi"
                value={formatNumber(stats.mailPending)}
                hint="Game server gửi mỗi 10 giây"
              />
            </Grid>
            <Grid item xs={6} md={4}>
              <Stat label="Giao dịch thử hôm nay" value={formatNumber(stats.testToday)} />
            </Grid>
          </Grid>
        </PageCard>
      </Grid>
    </Grid>
  );
}

Overview.propTypes = {
  data: PropTypes.shape({
    config: PropTypes.object,
    stats: PropTypes.object,
    testMode: PropTypes.bool,
  }).isRequired,
  onChanged: PropTypes.func.isRequired,
};

function OrdersTab({ canTest, onChanged, onShowUnmatched }) {
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(20);
  const [run, busy] = useRunner();
  const list = useFetch(
    () => api.get("/api/admin/payment/orders", { status, q: search, page, size }),
    [status, search, page, size]
  );

  const simulate = async (order) => {
    const { ok } = await run(
      () =>
        api.post("/api/admin/payment/test/transfer", { code: order.code, amount: order.amount }),
      (r) => RESULT_TEXT[r.result] || r.result
    );
    if (ok) {
      list.reload(true);
      onChanged();
    }
  };

  return (
    <PageCard
      title="Đơn nạp"
      subtitle="Người chơi tạo ở trang Nạp tiền trên website, hoặc kỹ thuật tạo ở tab Thử nạp"
      noPadding
    >
      <MDBox px={2} pt={2}>
        <Alert
          severity="info"
          action={
            <MDButton size="small" variant="text" color="info" onClick={onShowUnmatched}>
              Xem tiền chưa khớp
            </MDButton>
          }
        >
          <b>Khách báo đã chuyển tiền mà chưa nhận Pet Coin?</b> Tìm theo tên tài khoản bên dưới.
          Thấy đơn <b>Thành công</b> là đã gửi thư Pet Coin — nhắc khách mở hòm thư. Không thấy,
          hoặc đơn đã hủy: khách có thể chuyển sai nội dung, xem mục <b>tiền chưa khớp đơn</b> rồi
          bấm Gán.
        </Alert>
      </MDBox>
      <MDBox
        component="form"
        px={2}
        pt={2}
        onSubmit={(e) => {
          e.preventDefault();
          setPage(1);
          setSearch(q.trim());
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={4} md={3}>
            <SelectField
              fullWidth
              label="Trạng thái"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="all">Tất cả</MenuItem>
              <MenuItem value="waiting">Chờ chuyển khoản</MenuItem>
              <MenuItem value="paid">Thành công</MenuItem>
              <MenuItem value="cancelled">Đã hủy</MenuItem>
            </SelectField>
          </Grid>
          <Grid item xs={12} sm={5} md={4}>
            <MDInput
              fullWidth
              label="Tên tài khoản hoặc mã đơn"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
          </Grid>
          <Grid item xs="auto">
            <MDButton type="submit" variant="gradient" color="info">
              Tìm
            </MDButton>
          </Grid>
        </Grid>
        {list.error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {list.error}
          </Alert>
        )}
      </MDBox>
      <AdminTable
        loading={list.loading}
        rows={list.data?.items || []}
        rowKey="id"
        page={page}
        size={size}
        total={list.data?.total ?? 0}
        onPageChange={setPage}
        onSizeChange={(s) => {
          setSize(s);
          setPage(1);
        }}
        emptyText="Chưa có đơn nạp"
        columns={[
          { key: "code", label: "Mã đơn", render: (r) => <strong>{r.code}</strong> },
          {
            key: "username",
            label: "Tài khoản",
            render: (r) =>
              r.userId ? (
                <Link to={`/players/${r.userId}`}>{r.username || `tài khoản ${r.userId}`}</Link>
              ) : (
                r.username || "-"
              ),
          },
          {
            key: "amount",
            label: "Số tiền",
            align: "right",
            render: (r) => `${formatNumber(r.amount)}đ`,
          },
          {
            key: "coin",
            label: "Pet Coin",
            align: "right",
            render: (r) => (r.status === "paid" ? `+${formatNumber(r.coin)}` : "-"),
          },
          {
            key: "status",
            label: "Trạng thái",
            render: (r) => (
              <Chip
                size="small"
                color={ORDER_STATUS[r.status]?.color}
                label={ORDER_STATUS[r.status]?.label || r.status}
              />
            ),
          },
          { key: "createdAt", label: "Tạo lúc", render: (r) => formatDate(r.createdAt) },
          { key: "paidAt", label: "Tiền về", render: (r) => formatDate(r.paidAt) },
          canTest && {
            key: "actions",
            label: "",
            align: "right",
            render: (r) =>
              r.status !== "paid" && (
                <MDButton
                  size="small"
                  variant="text"
                  color="success"
                  disabled={busy}
                  onClick={() => simulate(r)}
                >
                  <Icon>science</Icon>&nbsp;Giả lập đã chuyển
                </MDButton>
              ),
          },
        ].filter(Boolean)}
      />
    </PageCard>
  );
}

OrdersTab.propTypes = {
  canTest: PropTypes.bool.isRequired,
  onChanged: PropTypes.func.isRequired,
  onShowUnmatched: PropTypes.func.isRequired,
};

const notifyBaseColumns = [
  { key: "receivedAt", label: "Thời gian", render: (r) => formatDate(r.receivedAt) },
  {
    key: "amount",
    label: "Số tiền",
    align: "right",
    render: (r) => (
      <MDBox display="flex" gap={0.5} justifyContent="flex-end" alignItems="center">
        {r.test && <Chip size="small" color="warning" label="Thử" />}
        {formatNumber(r.amount)}đ
      </MDBox>
    ),
  },
  { key: "content", label: "Nội dung chuyển khoản", wrap: true },
  { key: "orderCode", label: "Đơn", render: (r) => r.orderCode || "-" },
  { key: "username", label: "Tài khoản", render: (r) => r.username || "-" },
  {
    key: "coin",
    label: "Pet Coin",
    align: "right",
    render: (r) => (r.coin ? `+${formatNumber(r.coin)}` : "-"),
  },
  {
    key: "statusText",
    label: "Kết quả",
    render: (r) => (
      <Chip size="small" color={NOTIFY_COLOR[r.status] || "default"} label={r.statusText} />
    ),
  },
  { key: "note", label: "Ghi chú", wrap: true },
];

const PENDING_STATUS = [0, 2];

function NotifiesTab({ data, onChanged, initialStatus }) {
  const canResolve = Boolean(data?.canResolve);
  const vndPerCoin = data?.config?.vndPerCoin || 100;
  const [status, setStatus] = useState(initialStatus);
  const [channel, setChannel] = useState(initialStatus === "unmatched" ? "real" : "all");
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(20);
  const [run, busy] = useRunner();
  // dialog: { mode: "assign" | "ignore", row }
  const [dialog, setDialog] = useState(null);
  const [form, setForm] = useState({ orderCode: "", username: "", reason: "" });
  const list = useFetch(
    () => api.get("/api/admin/payment/notifies", { status, channel, page, size }),
    [status, channel, page, size]
  );

  const open = (mode, row) => {
    setForm({ orderCode: "", username: "", reason: "" });
    setDialog({ mode, row });
  };

  const submit = async () => {
    const { mode, row } = dialog;
    const { ok } = await run(
      () =>
        mode === "assign"
          ? api.post(`/api/admin/payment/notifies/${row.id}/assign`, {
              orderCode: form.orderCode.trim(),
              username: form.username.trim(),
              reason: form.reason.trim(),
            })
          : api.post(`/api/admin/payment/notifies/${row.id}/ignore`, {
              reason: form.reason.trim(),
            }),
      (r) =>
        mode === "assign"
          ? `Đã gán cho ${r.username}: game server gửi thư ${formatNumber(
              r.coin
            )} Pet Coin trong ~10 giây`
          : "Đã đánh dấu bỏ qua"
    );
    if (ok) {
      setDialog(null);
      list.reload(true);
      onChanged();
    }
  };

  const columns = canResolve
    ? [
        ...notifyBaseColumns,
        {
          key: "actions",
          label: "",
          render: (r) =>
            PENDING_STATUS.includes(r.status) && (
              <MDBox display="flex" gap={0.5}>
                <MDButton size="small" color="info" onClick={() => open("assign", r)}>
                  Gán
                </MDButton>
                <MDButton
                  size="small"
                  variant="outlined"
                  color="secondary"
                  onClick={() => open("ignore", r)}
                >
                  Bỏ qua
                </MDButton>
              </MDBox>
            ),
        },
      ]
    : notifyBaseColumns;

  const row = dialog?.row;
  const reasonOk = form.reason.trim().length >= 5;
  const targetOk = form.orderCode.trim() || form.username.trim();

  return (
    <PageCard
      title="Giao dịch ngân hàng báo về"
      subtitle="Mỗi lần SePay (hoặc lệnh test) báo một khoản tiền. Giao dịch không khớp đơn: bấm Gán để gửi Pet Coin cho đúng người, hoặc Bỏ qua kèm lý do"
      noPadding
    >
      <MDBox px={2} pt={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4} md={3}>
            <SelectField
              fullWidth
              label="Kết quả"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="all">Tất cả</MenuItem>
              <MenuItem value="credited">Đã ghi nhận</MenuItem>
              <MenuItem value="unmatched">Không khớp đơn (cần xử lý)</MenuItem>
              <MenuItem value="belowmin">Dưới mức tối thiểu</MenuItem>
              <MenuItem value="ignored">Bỏ qua</MenuItem>
            </SelectField>
          </Grid>
          <Grid item xs={12} sm={4} md={3}>
            <SelectField
              fullWidth
              label="Nguồn"
              value={channel}
              onChange={(e) => {
                setChannel(e.target.value);
                setPage(1);
              }}
            >
              <MenuItem value="all">Tất cả</MenuItem>
              <MenuItem value="real">Tiền thật</MenuItem>
              <MenuItem value="test">Giao dịch thử</MenuItem>
            </SelectField>
          </Grid>
        </Grid>
        {list.error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {list.error}
          </Alert>
        )}
      </MDBox>
      <AdminTable
        loading={list.loading}
        rows={list.data?.items || []}
        rowKey="id"
        page={page}
        size={size}
        total={list.data?.total ?? 0}
        onPageChange={setPage}
        onSizeChange={(sz) => {
          setSize(sz);
          setPage(1);
        }}
        emptyText="Chưa có giao dịch"
        columns={columns}
      />
      {row && (
        <ActionDialog
          open
          title={
            dialog.mode === "assign"
              ? `Gán giao dịch ${formatNumber(row.amount)}đ cho người chơi`
              : `Bỏ qua giao dịch ${formatNumber(row.amount)}đ`
          }
          description={`Nội dung: ${row.content || "(trống)"} · ${formatDate(row.receivedAt)}`}
          onClose={() => setDialog(null)}
          onSubmit={submit}
          busy={busy}
          color={dialog.mode === "assign" ? "info" : "warning"}
          submitText={
            dialog.mode === "assign"
              ? `Gửi ${formatNumber(Math.floor(row.amount / vndPerCoin))} Pet Coin`
              : "Bỏ qua"
          }
          disabled={!reasonOk || (dialog.mode === "assign" && !targetOk)}
        >
          {dialog.mode === "assign" && (
            <>
              <Alert severity="warning">
                Người được gán nhận thư{" "}
                <b>{formatNumber(Math.floor(row.amount / vndPerCoin))} Pet Coin</b> trong khoảng 10
                giây, tính vào tổng nạp như nạp thường. <b>Không thu hồi được</b> — kiểm tra kỹ biên
                lai (số tiền, giờ chuyển) trước khi gán.
              </Alert>
              <MDInput
                fullWidth
                label="Mã đơn (nếu người chơi gửi kèm)"
                placeholder={`${data?.config?.codePrefix || "PR"}XXXXXX`}
                value={form.orderCode}
                onChange={(e) => setForm({ ...form, orderCode: e.target.value })}
              />
              <MDInput
                fullWidth
                label="Hoặc tên tài khoản"
                value={form.username}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
                helperText="Nhập cả hai thì kiểm tra đơn có đúng của tài khoản này không"
              />
            </>
          )}
          <MDInput
            fullWidth
            multiline
            minRows={2}
            label="Lý do (bắt buộc)"
            value={form.reason}
            onChange={(e) => setForm({ ...form, reason: e.target.value })}
            helperText={
              dialog.mode === "assign"
                ? "Vd: người chơi gửi ảnh biên lai qua Zalo, chuyển thiếu chữ SEVQR"
                : "Vd: chủ dự án chuyển thử, chuyển nhầm đã hoàn tiền"
            }
          />
          {(!reasonOk || (dialog.mode === "assign" && !targetOk)) && (
            <MDTypography variant="caption" color="text">
              {dialog.mode === "assign" && !targetOk
                ? "Nhập mã đơn hoặc tên tài khoản người nhận. "
                : ""}
              {!reasonOk ? "Lý do cần ít nhất 5 ký tự (để sau này đối soát)." : ""}
            </MDTypography>
          )}
        </ActionDialog>
      )}
    </PageCard>
  );
}

NotifiesTab.defaultProps = { data: null, initialStatus: "all" };

NotifiesTab.propTypes = {
  data: PropTypes.shape({
    canResolve: PropTypes.bool,
    config: PropTypes.object,
  }),
  onChanged: PropTypes.func.isRequired,
  initialStatus: PropTypes.string,
};

function money(amount, count) {
  if (!count) return "-";
  return `${formatNumber(amount)}đ (${formatNumber(count)})`;
}

const reconcileColumns = [
  { key: "day", label: "Ngày", render: (r) => <strong>{r.day}</strong> },
  {
    key: "amount",
    label: "Tiền vào",
    align: "right",
    render: (r) => <strong>{money(r.amount, r.count)}</strong>,
  },
  {
    key: "creditedAmount",
    label: "Đã ghi nhận",
    align: "right",
    render: (r) => money(r.creditedAmount, r.creditedCount),
  },
  {
    key: "coin",
    label: "Pet Coin",
    align: "right",
    render: (r) => (r.coin ? formatNumber(r.coin) : "-"),
  },
  {
    key: "unmatchedAmount",
    label: "Không khớp",
    align: "right",
    render: (r) =>
      r.unmatchedCount ? (
        <MDTypography variant="button" color="error" fontWeight="medium">
          {money(r.unmatchedAmount, r.unmatchedCount)}
        </MDTypography>
      ) : (
        "-"
      ),
  },
  {
    key: "belowAmount",
    label: "Dưới tối thiểu",
    align: "right",
    render: (r) => money(r.belowAmount, r.belowCount),
  },
  {
    key: "ignoredAmount",
    label: "Bỏ qua",
    align: "right",
    render: (r) => money(r.ignoredAmount, r.ignoredCount),
  },
  {
    key: "mailPending",
    label: "Thư chờ gửi",
    align: "right",
    render: (r) => (r.mailPending ? formatNumber(r.mailPending) : "-"),
  },
];

function ReconcileTab() {
  const [days, setDays] = useState(31);
  const report = useFetch(() => api.get("/api/admin/payment/reconcile", { days }), [days]);
  const items = report.data?.items || [];
  const total = report.data?.total;
  return (
    <PageCard
      title="Đối soát theo ngày"
      subtitle="Chỉ tiền thật. So cột Tiền vào với sao kê ngân hàng cùng ngày: sao kê nhiều hơn là có giao dịch SePay không báo về (thiếu SEVQR, webhook lỗi). Bỏ qua gồm tiền ra, sai tài khoản, đã xử lý tay"
      noPadding
    >
      <MDBox px={2} pt={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4} md={3}>
            <SelectField
              fullWidth
              label="Khoảng thời gian"
              value={days}
              onChange={(e) => setDays(Number(e.target.value))}
            >
              <MenuItem value={7}>7 ngày</MenuItem>
              <MenuItem value={31}>31 ngày</MenuItem>
              <MenuItem value={92}>3 tháng</MenuItem>
              <MenuItem value={366}>1 năm</MenuItem>
            </SelectField>
          </Grid>
        </Grid>
        {report.error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {report.error}
          </Alert>
        )}
      </MDBox>
      <AdminTable
        loading={report.loading}
        rows={total && items.length > 0 ? [...items, { ...total, day: "Tổng" }] : items}
        rowKey="day"
        emptyText="Chưa có giao dịch tiền thật trong khoảng này"
        columns={reconcileColumns}
      />
    </PageCard>
  );
}

function TestTab({ data, onChanged }) {
  const { config } = data;
  const [run, busy] = useRunner();
  const [orderForm, setOrderForm] = useState({ username: "", amount: 20000 });
  const [form, setForm] = useState({
    code: "",
    amount: 20000,
    content: "",
    refId: "",
    transferType: "in",
  });
  const [last, setLast] = useState(null);
  const set = (patch) => setForm({ ...form, ...patch });

  const createOrder = async () => {
    const { ok, result } = await run(
      () =>
        api.post("/api/admin/payment/test/order", {
          username: orderForm.username.trim(),
          amount: Number(orderForm.amount),
        }),
      (r) => `Đã tạo đơn ${r.code}`
    );
    if (ok) {
      setForm({
        ...form,
        code: result.code,
        amount: Number(orderForm.amount),
        content: "",
        refId: "",
      });
      onChanged();
    }
  };

  const send = async (override = {}) => {
    const body = { ...form, ...override };
    const { ok, result } = await run(
      () =>
        api.post("/api/admin/payment/test/transfer", {
          code: body.code.trim(),
          amount: Number(body.amount),
          content: body.content.trim(),
          refId: body.refId.trim(),
          transferType: body.transferType,
        }),
      (r) => RESULT_TEXT[r.result] || r.result
    );
    if (ok) {
      setLast(result);
      onChanged();
    }
  };

  const presets = [
    { label: "Đúng mã, đủ tiền", icon: "check_circle", run: () => send({ refId: "" }) },
    {
      label: "Gửi lại giao dịch vừa rồi",
      icon: "replay",
      hint: "Thử chống cộng trùng",
      disabled: !last,
      run: () => send({ refId: last?.refId || "" }),
    },
    {
      label: "Không có mã đơn",
      icon: "help",
      run: () => send({ code: "", content: "nap tien game", refId: "" }),
    },
    {
      label: "Dưới mức tối thiểu",
      icon: "money_off",
      run: () => send({ amount: Math.max(1000, config.minAmount - 5000), refId: "" }),
    },
    { label: "Tiền ra", icon: "north_east", run: () => send({ transferType: "out", refId: "" }) },
  ];

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Alert severity="warning">
          Lệnh test đi qua đúng code xử lý webhook SePay: đơn chuyển &ldquo;Thành công&rdquo; và thư
          Pet Coin được gửi thật vào hòm thư người chơi, cộng cả tổng nạp. Giao dịch ghi nguồn
          &ldquo;Thử&rdquo;. Chỉ dùng trên dev hoặc server chưa có người chơi thật, tắt bằng
          paymentTestMode trong server.json.
        </Alert>
      </Grid>
      <Grid item xs={12} md={5}>
        <PageCard title="1. Tạo đơn thử" subtitle="Như người chơi bấm một gói ở trang Nạp tiền">
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <MDInput
                fullWidth
                label="Tên tài khoản"
                value={orderForm.username}
                onChange={(e) => setOrderForm({ ...orderForm, username: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <MDInput
                fullWidth
                type="number"
                label="Số tiền (đ)"
                value={orderForm.amount}
                inputProps={{ min: config.minAmount, step: 1000 }}
                helperText={`= ${formatNumber(
                  Math.floor((Number(orderForm.amount) || 0) / config.vndPerCoin)
                )} Pet Coin`}
                onChange={(e) => setOrderForm({ ...orderForm, amount: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <MDButton
                variant="gradient"
                color="info"
                disabled={busy || !orderForm.username.trim()}
                onClick={createOrder}
              >
                <Icon>add</Icon>&nbsp;Tạo đơn
              </MDButton>
            </Grid>
          </Grid>
        </PageCard>
      </Grid>
      <Grid item xs={12} md={7}>
        <PageCard
          title="2. Giả lập ngân hàng báo tiền vào"
          subtitle="Định dạng giống webhook SePay"
        >
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <MDInput
                fullWidth
                label="Mã đơn"
                value={form.code}
                placeholder={`${config.codePrefix}XXXXXX`}
                onChange={(e) => set({ code: e.target.value.toUpperCase() })}
              />
            </Grid>
            <Grid item xs={6}>
              <MDInput
                fullWidth
                type="number"
                label="Số tiền (đ)"
                value={form.amount}
                inputProps={{ min: 1 }}
                onChange={(e) => set({ amount: e.target.value })}
              />
            </Grid>
            <Grid item xs={12}>
              <MDInput
                fullWidth
                label="Nội dung chuyển khoản (bỏ trống: '<mã đơn> chuyen tien')"
                value={form.content}
                onChange={(e) => set({ content: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <MDInput
                fullWidth
                label="Mã giao dịch ngân hàng (bỏ trống: tự sinh)"
                value={form.refId}
                onChange={(e) => set({ refId: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <SelectField
                fullWidth
                label="Loại"
                value={form.transferType}
                onChange={(e) => set({ transferType: e.target.value })}
              >
                <MenuItem value="in">Tiền vào</MenuItem>
                <MenuItem value="out">Tiền ra</MenuItem>
              </SelectField>
            </Grid>
            <Grid item xs={12}>
              <MDButton variant="gradient" color="success" disabled={busy} onClick={() => send()}>
                <Icon>send</Icon>&nbsp;Gửi giao dịch
              </MDButton>
            </Grid>
          </Grid>
          <MDTypography variant="button" fontWeight="medium" display="block" mt={3} mb={1}>
            Kịch bản nhanh (dùng mã đơn và số tiền ở trên)
          </MDTypography>
          <MDBox display="flex" gap={1} flexWrap="wrap">
            {presets.map((p) => (
              <MDButton
                key={p.label}
                size="small"
                variant="outlined"
                color="info"
                disabled={busy || p.disabled}
                title={p.hint}
                onClick={p.run}
              >
                <Icon>{p.icon}</Icon>&nbsp;{p.label}
              </MDButton>
            ))}
          </MDBox>
          {last && (
            <Alert severity={last.result === "Credited" ? "success" : "info"} sx={{ mt: 2 }}>
              <strong>{RESULT_TEXT[last.result] || last.result}</strong>
              {last.notify && (
                <>
                  <br />
                  Mã giao dịch: {last.notify.providerRef}
                  {last.notify.orderCode && ` · Đơn ${last.notify.orderCode}`}
                  {last.notify.username && ` · ${last.notify.username}`}
                  {last.notify.coin > 0 && ` · ${formatNumber(last.notify.coin)} Pet Coin`}
                  <br />
                  {last.notify.note}
                </>
              )}
            </Alert>
          )}
        </PageCard>
      </Grid>
    </Grid>
  );
}

TestTab.propTypes = {
  data: PropTypes.shape({ config: PropTypes.object }).isRequired,
  onChanged: PropTypes.func.isRequired,
};

const TABS = ["orders", "notifies", "reconcile", "test"];

function Payment() {
  const [params, setParams] = useSearchParams();
  const tab = TABS.includes(params.get("tab")) ? params.get("tab") : "orders";
  const notifyFilter = params.get("filter") || "all";
  const overview = useFetch(() => api.get("/api/admin/payment/overview"), [], 15000);
  const data = overview.data;
  const reload = () => overview.reload(true);
  const unmatched = data?.stats?.unmatched || 0;

  const go = (next, filter) => {
    const p = new URLSearchParams();
    if (next !== "orders") p.set("tab", next);
    if (filter) p.set("filter", filter);
    setParams(p, { replace: true });
  };
  const showUnmatched = () => go("notifies", "unmatched");

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <PageGuide page="payment" />
        <MDBox display="flex" flexDirection="column" gap={1} mb={3}>
          {overview.error && (
            <Alert severity="error">Không tải được thông tin nạp tiền: {overview.error}</Alert>
          )}
          {unmatched > 0 && (
            <Alert
              severity="error"
              action={
                <MDButton size="small" variant="outlined" color="error" onClick={showUnmatched}>
                  Xử lý ngay
                </MDButton>
              }
            >
              Có <b>{formatNumber(unmatched)} khoản tiền thật</b> đã vào tài khoản nhưng chưa khớp
              đơn nào — người chơi chưa nhận Pet Coin. Cần gán cho đúng người hoặc bỏ qua kèm lý do.
            </Alert>
          )}
          {data && !data.config.enabled && (
            <Alert severity="warning">
              Kênh nạp đang <b>tắt</b>: người chơi không tạo được đơn nạp mới.
            </Alert>
          )}
          {data?.testMode && (
            <Alert severity="warning">
              Chế độ nạp thử đang <b>bật</b> trên máy chủ này (lệnh test gửi Pet Coin thật). Chỉ nên
              bật trên máy chủ test.
            </Alert>
          )}
        </MDBox>
        {data && <Overview data={data} onChanged={reload} />}
        <Card sx={{ mb: 3 }}>
          <Tabs
            value={tab}
            onChange={(e, value) => go(value)}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab
              value="orders"
              label="Đơn nạp"
              sx={{ flex: { xs: "0 0 auto", lg: "1 1 auto" }, px: 1.5 }}
            />
            <Tab
              value="notifies"
              label={
                unmatched > 0
                  ? `Giao dịch ngân hàng (${unmatched} cần xử lý)`
                  : "Giao dịch ngân hàng"
              }
              sx={{ flex: { xs: "0 0 auto", lg: "1 1 auto" }, px: 1.5 }}
            />
            <Tab
              value="reconcile"
              label="Đối soát"
              sx={{ flex: { xs: "0 0 auto", lg: "1 1 auto" }, px: 1.5 }}
            />
            <Tab
              value="test"
              label="Thử nạp (kỹ thuật)"
              sx={{ flex: { xs: "0 0 auto", lg: "1 1 auto" }, px: 1.5 }}
            />
          </Tabs>
        </Card>
        {tab === "orders" && (
          <OrdersTab
            canTest={Boolean(data?.canTest)}
            onChanged={reload}
            onShowUnmatched={showUnmatched}
          />
        )}
        {tab === "notifies" && (
          <NotifiesTab
            key={notifyFilter}
            data={data}
            onChanged={reload}
            initialStatus={notifyFilter}
          />
        )}
        {tab === "reconcile" && <ReconcileTab />}
        {tab === "test" &&
          data &&
          (data.canTest ? (
            <TestTab data={data} onChanged={reload} />
          ) : (
            <Alert severity="info">
              {data.testMode
                ? 'Tài khoản của bạn chưa có quyền "Gửi giao dịch nạp thử" (payment.test).'
                : 'Lệnh test đang tắt. Bật bằng "paymentTestMode": true trong config/server.json của game server rồi khởi động lại. Chỉ bật trên dev hoặc server chưa có người chơi thật.'}
            </Alert>
          ))}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Payment;
